import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'GuideAutoFrance.fr';
const DEFAULT_DESC = 'Vendez, achetez et estimez votre voiture à Montélimar. Dépôt-vente automobile et conseils professionnels.';
const DEFAULT_IMG = 'https://guideautofrance.fr/logo-guideautofrance.png';

const CANONICAL_BASE = 'https://guideautofrance.fr';

export default function SEOHead({ title, description, url, image, type = 'website', schemas = [], noindex = false, children }) {
  const fullTitle = title || `Vente et Estimation Auto à Montélimar | ${SITE_NAME}`;
  const desc = description || DEFAULT_DESC;
  const img = image || DEFAULT_IMG;
  const pageUrl = url || (CANONICAL_BASE + window.location.pathname);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD Schemas */}
      {schemas.map((schema, i) => (
        <script key={schema?.['@type'] || `schema-${i}`} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}

      {children}
    </Helmet>
  );
}

// Schema helpers
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "GuideAutoFrance.fr",
  "url": "https://guideautofrance.fr",
  "description": "Vendez, achetez et estimez votre voiture à Montélimar. Dépôt-vente automobile local.",
  "publisher": { "@type": "Organization", "name": "GuideAutoFrance" },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://guideautofrance.fr/blog?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Je Vends Votre Auto Montélimar",
  "url": "https://guideautofrance.fr",
  "logo": "https://guideautofrance.fr/logo-guideautofrance.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+33-4-75-52-98-20",
    "contactType": "customer service",
    "areaServed": "FR",
    "availableLanguage": "French"
  },
  "sameAs": [
    "https://www.facebook.com/people/Je-Vends-Votre-Auto-Mont%C3%A9limar/61582563750621",
    "https://www.instagram.com/symphony_motors_jvva/",
    "https://youtube.com/playlist?list=PLI2LWx_hMJWi0t09gwbs3OpJpXJlW6xCo"
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "name": "Je Vends Votre Auto Montélimar",
  "@id": "https://guideautofrance.fr/#business",
  "url": "https://guideautofrance.fr",
  "description": "Agence de transactions automobiles à Montélimar. Vente, achat, estimation, dépôt-vente et carte grise. Franchise jevendsvotreauto.com.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rue Raymond Louis",
    "addressLocality": "Montélimar",
    "addressRegion": "Drôme",
    "postalCode": "26200",
    "addressCountry": "FR"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 44.558,
    "longitude": 4.7508
  },
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "09:00", "closes": "12:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "14:00", "closes": "18:00" }
  ],
  "telephone": "+33-4-75-52-98-20",
  "email": "montelimar@jevendsvotreauto.com",
  "priceRange": "$$",
  "areaServed": { "@type": "City", "name": "Montélimar" },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "bestRating": "5",
    "ratingCount": "13"
  }
};

export function articleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt || article.meta_description || '',
    "image": article.featured_image || '',
    "datePublished": article.publish_date || '',
    "dateModified": article.update_date || article.publish_date || '',
    "author": { "@type": "Organization", "name": "GuideAutoFrance" },
    "publisher": {
      "@type": "Organization",
      "name": "GuideAutoFrance",
      "logo": { "@type": "ImageObject", "url": "https://guideautofrance.fr/logo-guideautofrance.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://guideautofrance.fr/blog/${article.slug}` }
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url ? `https://guideautofrance.fr${item.url}` : undefined
    }))
  };
}

export function webPageSchema(name, description, url) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": `https://guideautofrance.fr${url}`,
    "isPartOf": { "@type": "WebSite", "name": "GuideAutoFrance.fr" }
  };
}
